import { motion } from "framer-motion";

export default function Services() {
  const services = [
    {
      title: "Creative & Branding",
      icon: "🎨",
      accent: "from-teal-400 to-cyan-500",
      items: [
        "Graphic Design (Posters, Ads)",
        "Logo & Brand Identity",
        "Personal Branding",
        "UI/UX Architecture",
      ],
    },
    {
      title: "Development Services",
      icon: "💻",
      accent: "from-emerald-400 to-teal-600",
      items: [
        "Web Development (React)",
        "App Development (Android)",
        "Custom Software Solutions",
        "E-Commerce Architectures",
      ],
    },
    {
      title: "Growth & Marketing",
      icon: "🚀",
      accent: "from-cyan-400 to-emerald-500",
      items: [
        "Digital Marketing Strategy",
        "SEO Optimization",
        "Social Media Management",
        "Lead Generation & Ads",
      ],
    },
    {
      title: "AI & Automation",
      icon: "🤖",
      accent: "from-teal-500 to-teal-700",
      items: [
        "AI Chatbots for Business",
        "WhatsApp Automation",
        "Workflow Optimization",
        "AI Content Solutions",
      ],
    },
    {
      title: "Content & Media",
      icon: "📸",
      accent: "from-emerald-300 to-teal-500",
      items: [
        "Content Writing & Blogs",
        "Product Descriptions",
        "Ad Copy & Captions",
        "Professional Video Editing",
      ],
    },
    {
      title: "Support & Virtual",
      icon: "🛠️",
      accent: "from-teal-600 to-cyan-700",
      items: [
        "Website Maintenance",
        "Bug Fixing & Updates",
        "24/7 Customer Support",
        "Virtual Assistant Services",
      ],
    },
  ];

  const steps = [
    { title: "Requirement", desc: "Understanding your vision", icon: "📝" },
    { title: "Planning", desc: "Strategic architecture", icon: "📊" },
    { title: "Execution", desc: "Building the solution", icon: "⚙️" },
    { title: "Delivery", desc: "QA & Deployment", icon: "📦" },
  ];

  return (
    <section id="services" className="relative py-20 px-6 bg-[#f8fdfd] text-slate-800 overflow-hidden">
      {/* BACKGROUND */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-100/50 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-100/50 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* HEADER */}
        <div className="text-center mb-16">
          <motion.span 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-teal-600 font-black text-xs uppercase tracking-[0.5em] mb-4 block"
          >
            Our Ecosystem
          </motion.span>
          <h2 className="text-5xl md:text-6xl font-black text-slate-900 leading-tight">
            Comprehensive Digital <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500 italic">Solutions.</span>
          </h2>
        </div>

        {/* SERVICES GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -10 }}
              className="p-10 rounded-[3rem] bg-white border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.02)] hover:shadow-[0_30px_60px_rgba(13,148,136,0.1)] transition-all group"
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.accent} flex items-center justify-center text-3xl mb-8 shadow-lg shadow-teal-100 group-hover:rotate-12 transition-transform`}>
                {service.icon}
              </div>

              <h3 className="text-2xl font-black text-slate-900 mb-6 tracking-tight">
                {service.title}
              </h3>

              <ul className="space-y-4">
                {service.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-500 text-sm font-medium">
                    <span className="text-teal-500 font-bold">✦</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* WORKFLOW */}
        <div className="mt-24 relative">
          <div className="text-center mb-14">
            <h3 className="text-4xl font-black text-slate-900">How We Work</h3>
            <p className="text-slate-400 mt-2 font-medium uppercase tracking-widest text-xs">
              Aysu Groups Development Lifecycle
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-[2px] bg-teal-50 -z-10" />

            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.2 }}
                className="relative bg-white p-8 rounded-3xl border border-slate-50 shadow-sm text-center group hover:border-teal-300 transition-all"
              >
                <div className="w-16 h-16 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center text-3xl mb-6 group-hover:bg-teal-600 group-hover:text-white transition-all">
                  {step.icon}
                </div>
                <h4 className="text-lg font-black text-slate-900 mb-2 uppercase tracking-tighter">{step.title}</h4>
                <p className="text-slate-400 text-sm">{step.desc}</p>
                
                <div className="absolute -top-4 -right-4 w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-xs border-4 border-[#f8fdfd]">
                  0{i + 1}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mt-24 p-12 rounded-[4rem] bg-slate-900 text-white relative overflow-hidden text-center"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/20 rounded-full blur-[80px]" />
          
          <div className="relative z-10">
            <h3 className="text-4xl md:text-5xl font-black mb-6">
              Precision Services. <br />
              <span className="text-teal-400 italic">Competitive Pricing.</span>
            </h3>
            <p className="text-slate-400 max-w-xl mx-auto mb-8 text-lg">
              We specialize in delivering high-tier digital assets that fit your budget. Quality is our baseline, not a premium feature.
            </p>

            <button className="px-12 py-5 bg-teal-500 text-slate-900 rounded-2xl font-black tracking-widest shadow-xl shadow-teal-500/20 hover:bg-teal-400 hover:-translate-y-1 transition-all">
              INITIATE A PROJECT
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}